import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CompetitionState } from '../features/leaderboard/leaderboardApi';
import './CompetitionModeToggle.css';

interface ChapterOption {
  id: string;
  title: string;
}

interface CompetitionModeToggleProps {
  state: CompetitionState | undefined;
  chapters: ChapterOption[];
  busy: boolean;
  onActivate: (chapterId: string) => void;
  onDeactivate: () => void;
  onReset: () => void;
}

/** Instructor control to activate, reset or end a chapter-scoped competition (US7/US2, FR-004/FR-005/FR-043). */
function CompetitionModeToggle({
  state,
  chapters,
  busy,
  onActivate,
  onDeactivate,
  onReset,
}: CompetitionModeToggleProps) {
  const { t } = useTranslation();
  const [selectedChapterId, setSelectedChapterId] = useState('');

  const isActive = state?.active ?? false;
  const isFrozen = !isActive && Boolean(state?.endedAt);
  const activeChapterTitle = chapters.find((chapter) => chapter.id === state?.chapterId)?.title;

  function confirmThen(message: string, action: () => void) {
    if (window.confirm(message)) {
      action();
    }
  }

  function statusLabel(): string {
    if (isActive) {
      return t('leaderboard.statusActive');
    }
    if (isFrozen) {
      return t('leaderboard.statusFrozen');
    }
    return t('leaderboard.statusInactive');
  }

  return (
    <div className="competition-toggle">
      <p className="competition-toggle__status" role="status">
        {statusLabel()}
      </p>

      {!isActive && (
        <div className="competition-toggle__controls">
          <label htmlFor="competition-chapter-select">{t('leaderboard.scopeLabel')}</label>
          <select
            id="competition-chapter-select"
            value={selectedChapterId}
            onChange={(event) => setSelectedChapterId(event.target.value)}
          >
            <option value="" disabled>
              {t('leaderboard.selectChapterPlaceholder')}
            </option>
            {chapters.map((chapter) => (
              <option key={chapter.id} value={chapter.id}>
                {chapter.title}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || selectedChapterId === ''}
            onClick={() => onActivate(selectedChapterId)}
          >
            {t('leaderboard.activate')}
          </button>
        </div>
      )}

      {isActive && (
        <div className="competition-toggle__active">
          {activeChapterTitle && (
            <p className="competition-toggle__active-chapter">
              {t('leaderboard.activeChapterLabel', { title: activeChapterTitle })}
            </p>
          )}
          <div className="competition-toggle__actions">
            <button
              type="button"
              disabled={busy}
              onClick={() => confirmThen(t('leaderboard.resetConfirm'), onReset)}
            >
              {t('leaderboard.reset')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => confirmThen(t('leaderboard.endChallengeConfirm'), onDeactivate)}
            >
              {t('leaderboard.endChallenge')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompetitionModeToggle;
